import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X } from "lucide-react";
import { Reveal } from "./Reveal";
import { ASSETS, VIDEOS } from "../data";

const TILES = [
  { src: ASSETS.djBoothNight, span: "md:col-span-8 md:row-span-2", label: "Island House · Nassau" },
  { src: ASSETS.marinaSet, span: "md:col-span-4 md:row-span-1", label: "Marina Sessions" },
  { src: ASSETS.beachSet, span: "md:col-span-4 md:row-span-1", label: "Beachfront Set" },
  { src: ASSETS.headshot, span: "md:col-span-5 md:row-span-2", label: "Live by the Water", pos: "object-[center_25%]" },
  { src: ASSETS.headshotStudio, span: "md:col-span-7 md:row-span-2", label: "TAO LUMIN", pos: "object-[center_20%]" },
];

export const Gallery = () => {
  const [active, setActive] = useState(null); // index of open reel
  const modalVideoRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setActive(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    // lock body scroll + pause Lenis while lightbox is open
    if (active !== null) {
      window.__lenis?.stop?.();
      document.body.style.overflow = "hidden";
      // ensure it plays with sound once mounted
      const v = modalVideoRef.current;
      if (v) {
        v.muted = false;
        v.volume = 1;
        v.play().catch(() => {});
      }
    } else {
      window.__lenis?.start?.();
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [active]);

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="relative py-24 md:py-40"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-16">
          <Reveal>
            <p className="text-[#003399] font-bold text-xs uppercase tracking-[0.3em] mb-6">
              In the field
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display text-4xl md:text-6xl uppercase text-[#F2F2F2]">
              Gallery
            </h2>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 md:auto-rows-[200px] gap-4">
          {TILES.map((t, i) => (
            <Reveal
              key={i}
              delay={i * 0.1}
              className={`${t.span} group relative overflow-hidden`}
            >
              <div className="relative overflow-hidden h-[280px] md:h-full">
                <motion.img
                  src={t.src}
                  alt={t.label}
                  initial={{ scale: 1.15 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1] }}
                  className={`w-full h-full object-cover ${t.pos || ""} transition-transform duration-700 group-hover:scale-105`}
                />
                <div className="absolute inset-0 bg-black/20 group-hover:bg-[#003399]/25 transition-colors duration-500" />
                <div className="absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500">
                  <span className="font-display text-sm uppercase text-[#F2F2F2] tracking-wide">
                    {t.label}
                  </span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* In Motion — event video reels */}
        <div className="mt-24">
          <Reveal>
            <p className="text-[#003399] font-bold text-xs uppercase tracking-[0.3em] mb-6">
              In motion
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h3 className="font-display text-2xl md:text-4xl uppercase text-[#F2F2F2] mb-10">
              Live Reels
            </h3>
          </Reveal>

          <div className="grid grid-cols-1 gap-4 max-w-4xl">
            {VIDEOS.map((v, i) => (
              <Reveal key={i} delay={i * 0.08} className="group relative overflow-hidden">
                <button
                  type="button"
                  data-testid={`reel-open-${i}`}
                  onClick={() => setActive(i)}
                  aria-label={`Play ${v.label} reel`}
                  className="block w-full text-left"
                >
                  <div className="relative overflow-hidden bg-[#050505] aspect-video border border-white/10">
                    <video
                      data-testid={`gallery-video-${i}`}
                      src={v.src}
                      poster={`/videos/poster${i + 1}.jpg`}
                      muted
                      loop
                      playsInline
                      autoPlay
                      preload="metadata"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/15 group-hover:bg-[#003399]/20 transition-colors duration-500 pointer-events-none" />
                    {/* Play affordance */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <span className="h-16 w-16 md:h-20 md:w-20 rounded-full bg-[#003399]/80 backdrop-blur-sm flex items-center justify-center opacity-85 scale-90 group-hover:opacity-100 group-hover:scale-100 transition-all duration-300 shadow-[0_0_40px_rgba(0,51,153,0.7)]">
                        <Play size={28} className="text-[#F2F2F2] translate-x-[2px]" fill="currentColor" />
                      </span>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4 pointer-events-none">
                      <span className="font-display text-xs md:text-sm uppercase text-[#F2F2F2] tracking-wide drop-shadow-[0_1px_6px_rgba(0,0,0,0.95)]">
                        {v.label}
                      </span>
                    </div>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <p className="mt-10 text-[#F2F2F2]/40 text-xs uppercase tracking-[0.25em]">
              More visuals coming soon — follow @taolumin
            </p>
          </Reveal>
        </div>
      </div>

      {/* Reel lightbox */}
      <AnimatePresence>
        {active !== null && (
          <motion.div
            data-testid="reel-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            onClick={() => setActive(null)}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 md:p-10"
          >
            <button
              data-testid="reel-close"
              onClick={() => setActive(null)}
              aria-label="Close reel"
              className="absolute top-5 right-5 h-12 w-12 flex items-center justify-center border border-white/20 text-[#F2F2F2] hover:border-[#003399] hover:text-[#003399] transition-colors duration-300"
            >
              <X size={24} />
            </button>

            <motion.div
              initial={{ scale: 0.92, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-4xl"
            >
              <video
                ref={modalVideoRef}
                data-testid="reel-lightbox-video"
                key={VIDEOS[active].src}
                src={VIDEOS[active].src}
                poster={`/videos/poster${active + 1}.jpg`}
                controls
                autoPlay
                loop
                playsInline
                className="w-full max-h-[80vh] object-contain bg-black border border-white/10"
              />
              <div className="mt-4 flex items-center justify-between">
                <span className="font-display text-lg md:text-xl uppercase text-[#F2F2F2] tracking-wide">
                  {VIDEOS[active].label}
                </span>
                <span className="text-[10px] uppercase tracking-[0.25em] text-[#003399]">
                  TAO LUMIN · Live
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;
